import type { ReactNode } from "react";
import { isProblemType } from "@iamu/shared";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FindingFixDropdown } from "@/components/experience/finding-actions";
import { ProviderIcon } from "@/components/workflow/workflow-rail";
import type { useWorkRequests } from "@/components/experience/use-work-requests";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { MoreHorizontal } from "lucide-react";
import type { QueueRowData } from "./queue-types";

function ActionKbd({
  shortcut,
  className,
}: {
  shortcut: string;
  className?: string;
}) {
  return (
    <kbd
      title={`Shortcut: ${shortcut}`}
      className={cn(
        "ml-2 inline-flex h-4 min-w-4 items-center justify-center rounded border px-1 font-mono text-xs font-semibold shadow-2xs",
        className,
      )}
    >
      {shortcut}
    </kbd>
  );
}

export {
  PANE_BUTTONS,
  type PaneCommand,
  type PaneButtonDef,
} from "./queue-keys";
import { PANE_BUTTONS, type PaneCommand } from "./queue-keys";

export interface PaneActionsProps {
  row: QueueRowData;
  canEdit: boolean;
  projectId: string;
  hasLinkedRepo: boolean;
  requests: ReturnType<typeof useWorkRequests>;
  members?: Array<{ userId: string; displayName: string | null }>;
  onCommand: (c: PaneCommand) => void;
}

export function PaneActions({
  row,
  canEdit,
  projectId,
  hasLinkedRepo,
  requests,
  members,
  onCommand,
}: PaneActionsProps) {
  const { data: trackerTarget } = trpc.workflow.integrationTarget.get.useQuery(
    { projectId },
    { staleTime: 60_000 },
  );
  const { data: workflowData } = trpc.workflow.get.useQuery(
    { canonicalId: row.id },
    { staleTime: 30_000 },
  );

  if (!canEdit) {
    return (
      <p className="text-sm text-muted-foreground">
        Viewers can read findings; editors triage them.
      </p>
    );
  }

  const activeProvider =
    trackerTarget?.provider ?? (hasLinkedRepo ? "github" : null);
  const canTicket =
    canEdit && (Boolean(trackerTarget?.targetKey) || hasLinkedRepo);
  const hasLinkedIssue = Boolean(
    workflowData?.links && workflowData.links.length > 0,
  );

  const isOpenWork = [
    "new",
    "open",
    "in_progress",
    "regressed",
    "ready_to_fix",
  ].includes(row.state);
  const isProblem = isProblemType(row.type);

  const eligible = PANE_BUTTONS.filter((b) => !b.when || b.when(row));

  let primaryCommand: PaneCommand | null = null;
  if (row.state === "new") {
    primaryCommand = "accept";
  }

  const primaryBtn = eligible.find((b) => b.command === primaryCommand);
  const dismissBtn = eligible.find((b) => b.command === "dismiss");
  const createIssueBtn = eligible.find((b) => b.command === "createIssue");
  const linkIssueBtn = eligible.find((b) => b.command === "linkIssue");
  const overflowBtns = eligible.filter(
    (b) =>
      b !== primaryBtn &&
      b !== dismissBtn &&
      b !== createIssueBtn &&
      b !== linkIssueBtn,
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      {isOpenWork && isProblem && (
        <FindingFixDropdown
          findingId={row.id}
          projectId={projectId}
          hasLinkedRepo={hasLinkedRepo}
          state={row.state}
          requests={requests}
          open={requests.byCanonical.get(row.id) ?? null}
          assigneeName={
            row.assigneeId
              ? (members?.find((m) => m.userId === row.assigneeId)
                  ?.displayName ?? null)
              : null
          }
        />
      )}

      {primaryBtn && (
        <Button
          variant="outline"
          size="sm"
          aria-keyshortcuts={primaryBtn.key}
          onClick={() => onCommand(primaryBtn.command)}
        >
          {primaryBtn.label}
          <ActionKbd
            shortcut={primaryBtn.key}
            className="border-border bg-muted/60 text-muted-foreground"
          />
        </Button>
      )}

      {createIssueBtn && !hasLinkedIssue && (
        <Button
          variant="outline"
          size="sm"
          aria-keyshortcuts={createIssueBtn.key}
          onClick={() => onCommand(createIssueBtn.command)}
          disabled={!canTicket}
          title={
            !canTicket
              ? "Connect an issue tracker in Settings"
              : `Create issue (${createIssueBtn.key})`
          }
        >
          <ProviderIcon
            provider={activeProvider || undefined}
            className="mr-1.5 size-3.5"
          />
          {createIssueBtn.label}
          <ActionKbd
            shortcut={createIssueBtn.key}
            className="border-border bg-muted/60 text-muted-foreground"
          />
        </Button>
      )}

      {linkIssueBtn && !hasLinkedIssue && (
        <Button
          variant="outline"
          size="sm"
          aria-keyshortcuts={linkIssueBtn.key}
          onClick={() => onCommand(linkIssueBtn.command)}
          disabled={!canTicket}
          title={
            !canTicket
              ? "Connect an issue tracker in Settings"
              : `Link existing issue or PR (${linkIssueBtn.key})`
          }
        >
          {linkIssueBtn.label}
          <ActionKbd
            shortcut={linkIssueBtn.key}
            className="border-border bg-muted/60 text-muted-foreground"
          />
        </Button>
      )}

      {dismissBtn && (
        <Button
          variant="outline"
          size="sm"
          aria-keyshortcuts={dismissBtn.key}
          onClick={() => onCommand(dismissBtn.command)}
        >
          {dismissBtn.label}
          <ActionKbd
            shortcut={dismissBtn.key}
            className="border-border bg-muted/60 text-muted-foreground"
          />
        </Button>
      )}

      {overflowBtns.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              aria-label="More triage actions"
            >
              <MoreHorizontal className="size-4" aria-hidden="true" />
              <span>More</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {overflowBtns.map((b) => (
              <DropdownMenuItem
                key={b.command}
                onClick={() => onCommand(b.command)}
                className="flex items-center justify-between gap-4"
              >
                <span>{b.label}</span>
                <ActionKbd
                  shortcut={b.key}
                  className="border-border bg-muted text-muted-foreground"
                />
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
